/**
 * Google Analytics 4 Event Tracking Utility
 * Centralized analytics for key user interactions
 * 
 * GDPR Compliance:
 * - Uses Consent Mode v2 - analytics default to 'denied'
 * - Events are only sent after user grants consent via CookieConsentBanner
 * - Debug logging available in development mode
 */

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

export const GA_MEASUREMENT_ID = 'G-TM9X624KNW';

// Check if analytics is available
const isAnalyticsAvailable = (): boolean => {
  return typeof window !== 'undefined' && typeof window.gtag === 'function';
};

// Check if consent has been granted
const hasAnalyticsConsent = (): boolean => {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem('analytics_consent') === 'granted';
};

// Generic event tracker with debug logging
export function trackEvent(
  eventName: string,
  params?: Record<string, string | number | boolean | null | undefined>
): void {
  // Debug logging in development
  if (import.meta.env.DEV) {
    console.log('[GA4 Event]', eventName, params);
    
    if (!isAnalyticsAvailable()) {
      console.warn('[GA4] gtag not available - event not sent');
      return;
    }
    
    if (!hasAnalyticsConsent()) {
      console.warn('[GA4] Consent not granted - event queued but may not be processed');
    }
  }
  
  if (!isAnalyticsAvailable()) return;
  
  // Filter out null/undefined values
  const cleanParams = params
    ? Object.fromEntries(
        Object.entries(params).filter(([, v]) => v != null)
      )
    : undefined;
  
  window.gtag?.('event', eventName, cleanParams);
}

// Update consent state - called by CookieConsentContext
export function updateConsent(granted: boolean): void {
  if (!isAnalyticsAvailable()) return;
  
  window.gtag?.('consent', 'update', {
    'analytics_storage': granted ? 'granted' : 'denied',
  });
  
  if (import.meta.env.DEV) {
    console.log('[GA4 Consent] Updated to:', granted ? 'granted' : 'denied');
  }
}

// Page view tracking (enhanced)
export function trackPageView(
  pageName: string,
  pageLocation: string,
  userType?: 'guest' | 'member' | 'admin'
): void {
  trackEvent('page_view', {
    page_title: pageName,
    page_location: pageLocation,
    user_type: userType,
  });
}

// Navigation click tracking
export function trackNavigation(
  from: string,
  to: string,
  method: 'menu' | 'link' | 'command_palette' | 'mobile_menu' | 'dropdown'
): void {
  trackEvent('navigation_click', {
    from_page: from,
    to_page: to,
    navigation_method: method,
  });
}

// Authentication events
export function trackLogin(
  method: 'magic_link' | 'google',
  success: boolean
): void {
  trackEvent('login', {
    method,
    success,
  });
}

export function trackSignUp(method: 'magic_link' | 'google'): void {
  trackEvent('sign_up', {
    method,
  });
}

export function trackLogout(): void {
  trackEvent('logout');
}

// Form submission tracking
export function trackFormSubmit(
  formName: string,
  success: boolean,
  formData?: Record<string, string | number | boolean>
): void {
  trackEvent('form_submission', {
    form_name: formName,
    success,
    ...formData,
  });
}

// Button click tracking
export function trackButtonClick(
  buttonName: string,
  context?: string,
  additionalData?: Record<string, string | number | boolean>
): void {
  trackEvent('button_click', {
    button_name: buttonName,
    context,
    ...additionalData,
  });
}

// Feature engagement tracking
export function trackFeatureEngagement(
  featureName: string,
  action: 'open' | 'close' | 'interact' | 'complete'
): void {
  trackEvent('feature_engagement', {
    feature_name: featureName,
    action,
  });
}

// Chat interaction tracking
export function trackChatInteraction(
  action: 'open' | 'close' | 'send_message' | 'receive_response' | 'edit_message' | 'regenerate' | 'export_conversation' | 'export_all_conversations' | 'copy_conversation' | 'share_conversation'
): void {
  trackEvent('chat_interaction', {
    chat_name: 'chat_jci',
    action,
  });
}

// Content interaction tracking
export function trackContentInteraction(
  contentType: 'chart' | 'table' | 'map' | 'card' | 'accordion',
  contentId: string,
  action: 'sort' | 'filter' | 'expand' | 'collapse' | 'hover' | 'click'
): void {
  trackEvent('content_interaction', {
    content_type: contentType,
    content_id: contentId,
    action,
  });
}

// Download tracking
export function trackDownload(
  fileName: string,
  fileType: string,
  context?: string
): void {
  trackEvent('file_download', {
    file_name: fileName,
    file_type: fileType,
    context,
  });
}

// Preference change tracking
export function trackPreferenceChange(
  preferenceType: 'theme' | 'language' | 'notifications',
  oldValue: string,
  newValue: string
): void {
  trackEvent('preference_change', {
    preference_type: preferenceType,
    old_value: oldValue,
    new_value: newValue,
  });
}

// Onboarding tracking
export function trackOnboardingStep(
  step: number,
  stepName: string,
  action: 'view' | 'complete' | 'skip'
): void {
  trackEvent('onboarding_step', {
    step_number: step,
    step_name: stepName,
    action,
  });
}

export function trackOnboardingComplete(
  totalSteps: number,
  timeSpentSeconds?: number
): void {
  trackEvent('onboarding_complete', {
    total_steps: totalSteps,
    time_spent_seconds: timeSpentSeconds,
  });
}

// CTA click tracking (conversion-focused)
export function trackCTAClick(
  ctaName: string,
  ctaLocation: string,
  destination?: string
): void {
  trackEvent('cta_click', {
    cta_name: ctaName,
    cta_location: ctaLocation,
    destination,
  });
}

// Search tracking
export function trackSearch(
  searchQuery: string,
  resultsCount: number,
  searchContext: string
): void {
  trackEvent('search', {
    search_term: searchQuery,
    results_count: resultsCount,
    search_context: searchContext,
  });
}

// Scroll depth tracking
export function trackScrollDepth(
  depth: 25 | 50 | 75 | 90 | 100,
  pageName: string,
  pageLocation: string
): void {
  trackEvent('scroll_depth', {
    percent_scrolled: depth,
    page_title: pageName,
    page_location: pageLocation,
  });
}

// Time on page tracking
export function trackTimeOnPage(
  timeSeconds: number,
  pageName: string,
  pageLocation: string,
  exitMethod: 'navigation' | 'tab_close' | 'tab_hidden'
): void {
  trackEvent('time_on_page', {
    engagement_time_seconds: timeSeconds,
    page_title: pageName,
    page_location: pageLocation,
    exit_method: exitMethod,
  });
}

// Engagement score tracking (combined metric)
export function trackEngagementScore(
  score: number,
  category: 'highly_engaged' | 'engaged' | 'moderate' | 'low' | 'bounce',
  breakdown: {
    scroll_score: number;
    time_score: number;
    interaction_count: number;
    time_seconds: number;
    max_scroll_depth: number;
  },
  pageLocation: string
): void {
  trackEvent('engagement_score', {
    engagement_score: score,
    engagement_category: category,
    scroll_score: breakdown.scroll_score,
    time_score: breakdown.time_score,
    interaction_count: breakdown.interaction_count,
    time_seconds: breakdown.time_seconds,
    max_scroll_depth: breakdown.max_scroll_depth,
    page_title: document.title,
    page_location: pageLocation,
  });
}

// Export analytics object for convenience
export const analytics = {
  trackEvent,
  pageView: trackPageView,
  navigation: trackNavigation,
  login: trackLogin,
  signUp: trackSignUp,
  logout: trackLogout,
  formSubmit: trackFormSubmit,
  buttonClick: trackButtonClick,
  featureEngagement: trackFeatureEngagement,
  chatInteraction: trackChatInteraction,
  contentInteraction: trackContentInteraction,
  download: trackDownload,
  preferenceChange: trackPreferenceChange,
  onboardingStep: trackOnboardingStep,
  onboardingComplete: trackOnboardingComplete,
  ctaClick: trackCTAClick,
  search: trackSearch,
  scrollDepth: trackScrollDepth,
  timeOnPage: trackTimeOnPage,
  engagementScore: trackEngagementScore,
};
